import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useOverlayDepth } from 'modules/Common/utils/useOverlayDepth';

/**
 * The walkthrough, played where it was clicked — big, centred, on a dimmed page.
 *
 * Clicking a step's thumbnail used to open the item modal, which meant a title
 * bar, the step's markdown again and a footer wrapped around a player the size
 * of the modal's body. Nothing in that frame was worth the detour: the how-to
 * is already open in the card behind it. So this is the player and nothing
 * else — the page darkens, the video fills the middle of the screen, and the
 * step stays exactly where it was underneath.
 *
 * Portalled to `document.body` on purpose: both surfaces that show the path
 * clip their contents (`overflow-hidden` on Home's cell and on the floating
 * widget's column), so an overlay rendered in place would be trapped inside a
 * 420px panel. It takes a rung off the shared dialog scale rather than a number
 * of its own (`useOverlayDepth`) — that band already clears the floating
 * widget and the app chrome.
 */

// Where a walkthrough can come from. The seed carries a plain URL and nothing
// in it says which host it points at, so this is the one place that decides how
// to play one — the thumbnail and the item modal both import it.
const YOUTUBE =
  /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
const VIMEO = /vimeo\.com\/(?:video\/)?(\d+)/;
const LOOM = /loom\.com\/(?:share|embed)\/([\w-]+)/;
/** A URL we can hand to `<video>` ourselves — S3, CloudFront, R2, anything. */
const VIDEO_FILE = /\.(mp4|webm|ogv|ogg|mov|m4v)(?:[?#]|$)/i;

/**
 * How to play `url`, whoever hosts it.
 *
 * @param {string} url
 * @param {{autoplay?: boolean}} [options] `autoplay` belongs to the caller: a
 *   lightbox opened BY a click on the video should start playing, a modal that
 *   merely contains one should not start talking at the reader.
 * @returns {{kind: 'file'|'embed', src: string, poster: string|null}|null}
 *   `null` only when there is no URL at all.
 */
export const resolveVideo = (url, { autoplay = false } = {}) => {
  if (!url) return null;
  const play = autoplay ? 1 : 0;

  const youtube = url.match(YOUTUBE);
  if (youtube) {
    return {
      kind: 'embed',
      // `rel=0` keeps the end screen on this channel — an onboarding step
      // shouldn't hand a new declarant the open web.
      src: `https://www.youtube.com/embed/${youtube[1]}?autoplay=${play}&rel=0`,
      // The only host here that serves a still without an API round-trip.
      poster: `https://img.youtube.com/vi/${youtube[1]}/mqdefault.jpg`,
    };
  }

  const vimeo = url.match(VIMEO);
  if (vimeo) {
    return {
      kind: 'embed',
      src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=${play}`,
      poster: null,
    };
  }

  const loom = url.match(LOOM);
  if (loom) {
    return {
      kind: 'embed',
      src: `https://www.loom.com/embed/${loom[1]}?autoplay=${play}`,
      poster: null,
    };
  }

  // Ours to play: `<video>` rather than an iframe, so it gets native controls
  // and the thumbnail can pull its own first frame out of the file.
  if (VIDEO_FILE.test(url)) return { kind: 'file', src: url, poster: null };

  // Anything else is taken at face value as a player URL — better than a
  // "coming soon" placeholder for a host nobody taught this function about.
  // A provider that needs a query flag to autoplay just won't, until it's
  // named above.
  return { kind: 'embed', src: url, poster: null };
};

const OnboardingVideoLightbox = ({ videoUrl, title, onClose }) => {
  const { t } = useTranslation('onboarding');
  const closeRef = useRef(null);
  // Mounted only while playing, so it is open by definition. Depth 0 in every
  // real case — nothing here opens a dialog first — but taken from the stack
  // rather than assumed, so a future overlay above it still orders correctly.
  const { overlay: overlayZ } = useOverlayDepth(true);

  // Escape closes the video and nothing else. In CAPTURE, because the floating
  // widget listens for Escape on `document` too and would otherwise close the
  // whole panel out from under the player: a document-level capture listener
  // runs before any document-level bubble listener, whatever order they were
  // registered in, and stopping propagation there keeps the key from reaching
  // it. (Once focus is inside an embedded player the keystroke belongs to that
  // frame and never reaches us at all — hence the close button.)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  // Nothing behind the overlay is meant to move while it is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Focus lands on Close, so the overlay is dismissable by keyboard from the
  // first frame — the click came from a button that is now behind a scrim.
  useEffect(() => closeRef.current?.focus(), []);

  // Clicking the thumbnail IS the play gesture — a second click on a player
  // that just filled the screen would be a bug report.
  const video = resolveVideo(videoUrl, { autoplay: true });
  // Only reachable through a thumbnail that needs a URL to render at all.
  if (!video) return null;

  return createPortal(
    // The floating widget's click-outside handler reads this attribute to tell
    // "clicked away from the panel" from "clicked the scrim over it".
    <div
      data-onboarding-video-lightbox
      role="dialog"
      aria-modal="true"
      aria-label={t('video.walkthroughFor', '{{step}} walkthrough', { step: title })}
      onMouseDown={(e) => {
        // Scrim only: a drag that starts on the player and ends outside it
        // (scrubbing past the edge) must not count as clicking away.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{ zIndex: overlayZ }}
      className="fixed inset-0 flex items-center justify-center p-4 bg-slate-950/80 animate-in fade-in duration-150"
    >
      {/* 16:9, as large as the shorter of the two viewport axes allows — width
          first, then clamped by whatever height is left once the caption row
          and the page's padding are taken out, so the frame never runs off the
          bottom of a laptop screen. */}
      <div
        className="relative w-full"
        style={{ maxWidth: 'min(1120px, calc((100vh - 8rem) * 16 / 9))' }}
      >
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <span className="text-[13px] font-semibold text-white/90 truncate">
            {t('video.titleCaption', '{{step}} — walkthrough', { step: title })}
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            title={t('video.close', 'Close video')}
            aria-label={t('video.close', 'Close video')}
            className="w-[30px] h-[30px] shrink-0 inline-flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <FontAwesomeIcon
              icon={faTimes}
              className="text-[15px]"
            />
          </button>
        </div>

        <div className="aspect-video w-full rounded-xl overflow-hidden bg-black shadow-2xl">
          {video.kind === 'file' ? (
            // Autoplay after a click on the thumbnail is a gesture-backed play,
            // which every browser allows with sound. `controls` regardless, so
            // a policy that still blocks it leaves a play button rather than a
            // black rectangle.
            <video
              src={video.src}
              className="w-full h-full"
              controls
              autoPlay
              playsInline
            >
              <track kind="captions" />
            </video>
          ) : (
            <iframe
              src={video.src}
              title={title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OnboardingVideoLightbox;
